<template>
    <div class="row">
        <div class="col-12 col-md-12 note-head" :class="[isInlineRx && 'rx-head', isInvestigation && 'investigation-head']">
            <div class="note-title">
                <h4 class="title-main">
                    <span>{{ note.name }}</span>
                </h4>
                <div v-if="from === 'group-template'">
                    <div class="input-group input-group-merge group-template">
                        <input
                            v-model="note.template_name"
                            type="text"
                            name="name" 
                            placeholder="Enter template name"
                            class="form-control"
                        />
                    </div>
                </div>
                <AsyncSelect
                    v-if="note.hasOwnProperty('service_resource')"
                    class="select-doctor"
                    placeholder="Select Doctor"
                    v-model="note.service_resource"
                    :api-service="fetchServiceList"
                    :additional-query="{ resource_type: 'human_resources' }"
                />
                <div class="rx-search-wrapper" v-if="isInlineRx" ref="rxDropdownRef">
                    <div class="input-group input-group-merge">
                        <span class="input-group-text">
                            <img src="@/assets/search-icon.svg" alt="Search" width="14" height="14" />
                        </span>
                        <input
                            v-model="searchParams.q"
                            @input="onSearchProduct"
                            @focus="onFocusProductSearch"
                            @keyup.enter="onEnterProductSearch"
                            type="text"
                            class="form-control"
                            :placeholder="searchPlaceholder"
                        />
                    </div>
                    <ul v-if="isProductListOpen" class="inline-product-list deep-shadow" @scroll="onScrollProductList">
                        <li v-if="productLoading && !productResults.length" class="empty-text">Loading...</li>
                        <li v-if="!productLoading && !productResults.length" class="empty-text">{{ emptyText }}</li>
                        <li
                            v-for="product in productResults" :key="product.id"
                            @click="onSelectProduct(product)"
                            class="product-item"
                        >
                            <div class="form-check">
                                <input :checked="product.is_selected" class="form-check-input" type="checkbox">
                                <label :class="product.is_selected && 'text-primary text-bold'" class="form-check-label">
                                    <span>{{ product.name }}</span>
                                    <span v-if="product.product_type !== 'services'"> (stock: {{ product.stock_in_hand }})</span>
                                    <span v-if="!isInvestigation"> (price: ৳{{ getProductPrice(product) }})</span>
                                </label>
                            </div>
                        </li>
                    </ul>
                </div>
                <AsyncSelect
                    v-if="isInlineRx"
                    class="select-template"
                    placeholder="Select Template Name"
                    v-model="groupTemplate"
                    :additional-query="{ template_type: note.value }"
                    :api-service="fetchTemplateList"
                    @option:selected="onSelectTemplate"
                />
                <h4 class="prescription-date" :class="isInlineRx && 'rx-date'" v-if="!isInlineRx || note.date"><span v-if="note.date" class="note-date">{{ note.date }}</span></h4>
            </div>
            <div class="note-btn" v-if="!note.isHideAddBtn && !isInlineRx">
                <button @click="onOpenModal(note, index)" type="button" class="btn btn-outline-primary btn-sm">
                    <i class="fas fa-plus"></i>
                </button>
            </div>
            <div class="note-btn" v-if="isInlineRx && !isInvestigation">
                <button
                    @click="openProductAddModal"
                    type="button"
                    class="btn btn-primary btn-sm rx-add-btn"
                    title="Add new product"
                    aria-label="Add new product"
                >
                    <i class="fas fa-plus"></i>
                </button>
            </div>
        </div>
        <div class="col-12">
            <hr class="mt-0" :class="isInlineRx && 'rx-divider'">
        </div>
        <div class="col-12 col-md-12 note-body" :class="isInvestigation && 'investigation-body'">
            <div class="card">
                <div class="card-body">
                    <div class="rx-body" v-for="(item, noteIndex) in note.selectedNotes" :key="noteIndex">
                        <div class="medicine">
                            <div class="medicine-name">
                                <span v-if="note.value !== 'investigation'" class="item-checkbox">
                                    <input v-model="item.is_published" type="checkbox">
                                </span>
                                <span class="name-group" :class="getGroupClass(item)">
                                    <span class="sr-no">{{ noteIndex + 1 }}. </span>
                                    <span class="name">{{ item.name }}</span>
                                </span>
                            </div>
                            <div v-if="checkTerms(item)" @click="onRemoveNote(noteIndex, item)" class="action">
                                <i class="far fa-times-circle close"></i>
                            </div>
                        </div>
                        <div class="row px-2" :class="`${item.status === 'inactive' && 'disabled-element'}`">
                            <div v-for="medNote in noteObj.medicineNotes" :key="medNote.value" :class="`${col} dose`">
                                <button @click="openRxMedicineModal(medNote, noteIndex, item)" type="button" class="btn btn-sm btn-block">
                                    <span v-if="!item[medNote.primeValue]">
                                        {{ medNote.name }}
                                        <i class="fas fa-plus"></i>
                                    </span>
                                    <span v-if="item[medNote.primeValue]">
                                        {{ item[medNote.primeValue] }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-12 note-footer pb-2" v-if="from === 'group-template'">
            <SubmitCancelBtn
                class="text-right"
                @onSubmit="onSaveTemplate"
                :loading="loading"
                :isHideCancelBtn="true"
            />
        </div>
        <MedicineNoteModal
            v-if="isModalOpen"
            :noteObj="noteObj"
            @onHandleMedNote="onHandleMedNote"
            @onCloseModal="onCloseModal"
        />
        <ProductAddModal
            v-if="isProductModalOpen"
            @onClose="isProductModalOpen = false"
            @onProductAdded="onProductAdded"
        />
        <Loader v-if="templateLoading"/>
    </div>
</template>

<script setup>
import { inject, onMounted, reactive, ref } from "vue"
import { useStore } from "vuex";
import { debounce } from 'lodash';
import MedicineNoteModal from "./MedicineNoteModal.vue";
import ProductAddModal from "./ProductAddModal.vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import Loader from '@/components/atom/LoaderComponent'
import handleHospital from '@/services/modules/hospital'
import handlePurchase from '@/services/modules/purchase';
import useDetectOutsideClick from '@/services/useDetectOutsideClick';
import { computed } from "vue";
import { useRoute } from "vue-router";
import SubmitCancelBtn from '@/components/atom/SubmitCancelBtn.vue';

const props = defineProps({
    note: Object,
    index: Number,
    openModal: Function,
    medicineNotes: {
        type: Array,
        default: [],
    },
    from: {
        type: String,
        default: ''
    },
    onClickToPrintRx: {
        type: Function,
        default: () => {}
    },
    onSave: {
        type: Function,
        default: () => {}
    }
})

const emit = defineEmits(['openModal', 'onClickToPrintRx', 'onSave'])
const { fetchServiceList, storeGroupTemplate, updateGroupTemplate, deleteGroupTemplate, fetchTemplateList, fetchSingleTemplate } = handleHospital();
const { fetchProducts, fetchProductBySlug } = handlePurchase();
const store = useStore();
const route = useRoute();
const col = ref('col-3 col-md-3');
const isModalOpen = ref(false);
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false);
const templateForm = ref({
    name: '',
    template_type: ''
});
const isProductModalOpen = ref(false);
const templateLoading = ref(false);
const groupTemplate = ref(null);
const productResults = ref([]);
const scrollHeight = ref(0);
const isProductListOpen = ref(false);
const rxDropdownRef = ref(null);
const productLoading = ref(false);
const lastPage = ref(1);
const searchParams = reactive({
    page: 1,
    per_page: 100,
    q: '',
    product_type: 'finished_goods',
    service_type: 'rx'
});

const isInlineRx = computed(() => {
    if (props.from === 'group-template') return false;
    return ['rx', 'investigation'].includes(props.note.value);
})

const isInvestigation = computed(() => {
    return props.note.value === 'investigation';
})

const searchPlaceholder = computed(() => {
    if (isInvestigation.value) return 'Search investigation';
    return 'Search Drug';
})

const emptyText = computed(() => {
    if (isInvestigation.value) return 'No investigation found';
    return 'No medicine found';
})

const noteObj = reactive({
    currentTab: 'dose',
    medicineNotes: [],
    noteIndex: -1,
    selectedNote: '',
})

const getGroupClass = (item) => {
    let classText = '';

    if (item.status === 'inactive') {
        classText = 'text-with-divider disabled-element';
    }

    if (props.note.value === 'rx') {
        classText += ' margin-left';
    }

    return classText;
}

const checkTerms = (item) => {
    if(!props.from) return true;
    else if(['doctorOrder', 'group-template'].includes(props.from) && item.status === 'active') return true;
    return false;
}

const onOpenModal = (note, index) => {
    emit('openModal', {note, index})
}

const onHandleMedNote = ({note, newNote}) => {
    const currentTab = noteObj.currentTab === 'investigation_remarks' ? 'remarks' : noteObj.currentTab;
    props.note.selectedNotes[noteObj.noteIndex][currentTab] = note.name;
    if(newNote){
        props.note.newNotesByDoctor.push(newNote);
    }
}

const openRxMedicineModal = (item, index, selectedNote) => {
    noteObj.currentTab = item.value;
    noteObj.noteIndex = index;
    noteObj.selectedNote = selectedNote;
    isModalOpen.value = true;
}

const onRemoveNote = (selectedIndex, item) => {
    if(item.isEdited){
        item.status = item.status === 'active' ? 'inactive' : 'active'
        return;
    }
    props.note.selectedNotes.splice(selectedIndex, 1)
    if(!isInlineRx.value) return;
    const listedProduct = productResults.value.find(product => isSameProduct(item, product));
    if(listedProduct){
        listedProduct.is_selected = false;
    }
}

const setHideOrShowTabs = () => {
    noteObj.medicineNotes.length = 0;
    const filteredNotes = props.medicineNotes.filter(item => {
        item.primeValue = item.value
        if(item.value === 'investigation_remarks'){
            item.primeValue = 'remarks'
        }
        if (props.note.value === 'rx') {
            return !['investigation_remarks', 'unit'].includes(item.value);
        }
        return item.value === 'investigation_remarks';
    })
    noteObj.medicineNotes.push(...filteredNotes);
}

const getProductQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    for(let key in searchParams){
        query += `&${key}=${encodeURIComponent(searchParams[key])}`;
    }
    return query;
}

const isSameProduct = (note, product) => {
    if(note.product_id && product.id) return note.product_id === product.id;
    return note.name === product.name;
}

const isProductSelected = (product) => {
    return props.note.selectedNotes.some(note => isSameProduct(note, product));
}

const markSelectedProducts = (products) => {
    return products.map(product => {
        return {
            ...product,
            is_selected: isProductSelected(product)
        };
    });
}

const syncSelectedProducts = () => {
    productResults.value.forEach(product => {
        product.is_selected = isProductSelected(product);
    });
}

const getInvestigationQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&page=${searchParams.page}`;
    query += `&per_page=${searchParams.per_page}`;
    query += `&product_type=services&slug=investigation`;
    query += `&service_type=investigation`;
    query += `&q=${encodeURIComponent(searchParams.q)}`;
    return query;
}

const getProductListRequest = () => {
    if (isInvestigation.value) return fetchProductBySlug(getInvestigationQuery());
    return fetchProducts(getProductQuery());
}

const fetchProductList = async (isAppend = false) => {
    productLoading.value = true;
    await getProductListRequest().then(res => {
        if(!res.data || !res.data.data){
            lastPage.value = 1;
            if(!isAppend) productResults.value = [];
            return;
        }
        lastPage.value = res.data.last_page || 1;
        const formatted = markSelectedProducts(res.data.data);
        if(isAppend){
            productResults.value.push(...formatted);
            return;
        }
        productResults.value = formatted;
    }).catch(() => {
        if(!isAppend) productResults.value = [];
    }).finally(() => {
        productLoading.value = false;
    })
}

const onSearchProduct = debounce(() => {
    searchParams.page = 1;
    isProductListOpen.value = true;
    fetchProductList();
}, 300)

useDetectOutsideClick(rxDropdownRef, () => {
    closeProductList();
})

const onScrollProductList = (e) => {
    let percent = (e.target.scrollTop + e.target.clientHeight) / e.target.scrollHeight * 100;
    percent = parseInt(percent);
    const canLoadMore = percent >= 90 && scrollHeight.value < 90 && !productLoading.value && searchParams.page < lastPage.value;
    scrollHeight.value = percent;
    if (!canLoadMore) return;
    searchParams.page += 1;
    fetchProductList(true);
}

const getProductPrice = (product) => {
    if(product.price) return product.price;
    if(product.description && product.description.sales_price) return product.description.sales_price;
    return 0;
}

const getProductRemarks = (product) => {
    if(product.remarks) return product.remarks;
    if(product.instruction) return product.instruction;
    return '';
}

const getProductValue = (value) => {
    if(!value) return '';
    return value;
}

const makeMedicineObj = (product) => {
    return {
        id: 0,
        product_id: product.id,
        name: product.name,
        price: getProductPrice(product),
        dose: getProductValue(product.dose),
        unit: getProductValue(product.unit),
        duration: getProductValue(product.duration),
        remarks: getProductRemarks(product),
        madication_status: '',
        is_published: true,
        status: 'active',
        order_date: '',
        is_selected: true
    }
}

const closeProductList = () => {
    isProductListOpen.value = false;
}

const onEnterProductSearch = async () => {
    if (!searchParams.q) return;
    onSearchProduct.flush();
    await fetchProductList();
    const typed = searchParams.q.toLowerCase();
    const matched = productResults.value.find(function (product) {
        return product.name.toLowerCase() === typed;
    });
    if (!matched) return;
    onSelectProduct(matched);
    searchParams.q = '';
}

const onFocusProductSearch = () => {
    isProductListOpen.value = true;
}

const onSelectProduct = (product) => {
    const existingIndex = props.note.selectedNotes.findIndex(item => isSameProduct(item, product));
    if(existingIndex !== -1){
        props.note.selectedNotes.splice(existingIndex, 1);
        product.is_selected = false;
        closeProductList();
        return;
    }
    props.note.selectedNotes.push(makeMedicineObj(product));
    product.is_selected = true;
    closeProductList();
}

const onSelectTemplate = async (template) => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&template_type=${props.note.value}`;
    templateLoading.value = true;
    await fetchSingleTemplate(query, template.id).then(res => {
        if (!res.status) return showError(res.message);
        setTemplateItems(res.data);
    }).catch(err => {
        showError('Failed to load template');
    }).finally(() => {
        templateLoading.value = false;
        groupTemplate.value = null;
    })
}

const setTemplateItems = (products) => {
    for (const item of products) {
        const exists = props.note.selectedNotes.some(note => isSameProduct(note, item));
        if(exists) continue;
        props.note.selectedNotes.push(makeMedicineObj(item));
    }
    syncSelectedProducts();
}

const openProductAddModal = () => {
    isProductModalOpen.value = true;
}

const getCreatedProductName = (product) => {
    if(product.name) return product.name;
    return product.text;
}

const onProductAdded = (product) => {
    isProductModalOpen.value = false;
    const createdProduct = {
        id: product.id,
        name: getCreatedProductName(product),
        price: 0
    };
    props.note.selectedNotes.push(makeMedicineObj(createdProduct));
    searchParams.page = 1;
    fetchProductList();
}

const onSaveTemplate = async () => {
    const data = formatFormData();

    const id = props.note.id || null;
    const templateAction = id ? updateGroupTemplate : storeGroupTemplate;

    if(!data.name){
        return showError('Please enter name');
    }

    if(data.notes.length === 0){
        return showError('Please add at least one item.');
    }

    loading.value = true;
    await templateAction(data, id).then((res) => {
        if (!res.status) return showError(res.message);
        showSuccess(res.message);
        emit('onSave');
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const formatFormData = () => {
    const formattedData = {};
    formattedData.name = props.note.template_name;
    formattedData.company_id = route.params.companyId;
    formattedData.template_type = props.note.value;
    formattedData.is_common = props.note.is_common ? 1 : 0;
    
    formattedData.notes = props.note.selectedNotes.map((item) => {
            return {
                id: item.template_id || null,
                templateable_id: item.template_id ? item.templateable_id : item.product_id,
                additional_info: {
                    dose: item.dose,
                    unit: item.unit,
                    duration: item.duration,
                    remarks: item.remarks,
                    investigation_remarks: item.investigation_remarks
                }
            }
        });

    return formattedData;
}

const onCloseModal = () => {
    isModalOpen.value = false;
}

onMounted(async () => {
    if(props.note.template_name){
        templateForm.value.name = props.note.template_name;
    }

    setHideOrShowTabs()

    if(props.note.value === 'investigation'){
        col.value = 'col-12 col-md-12'
    }

    if(props.note.value === 'rx'){
        col.value = 'col-12 col-sm-4 col-md-4'
    }

    if(isInlineRx.value){
        await fetchProductList();
    }
})

</script>

<style scoped>
.item-checkbox {
    position: relative;

    input {
        position: absolute;
        top: 21%;
    }
        
}
.name-group.margin-left {
    margin-left: 21px;
}
.note-btn {
    text-align: right;
    width: 11%;
}
.group-template {
    width: 180%;
}
.note-title {
    width: 90%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.select-doctor {
    width: 48%;
}
.prescription-date {
    width: 34%;
}
.prescription-date.rx-date {
    width: auto;
    margin-bottom: 0;
}
.note-date {
    font-size: 16px;
    margin-left: 2%;
}
.rx-body .row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0;
}
.rx-body .row > *{
    padding: 0 !important;
    flex: 0 0 auto;
    width: auto !important;
    max-width: 100% !important;
    min-width: 0;
}
.investigation-body .rx-body {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 10px;
    max-width: 100%;
}
.note-body {
    min-width: 0;
    max-width: 100%;
}
.investigation-body .rx-body .medicine {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    justify-content: flex-start;
    gap: 6px;
}
.investigation-body .rx-body .medicine .name-group {
    min-width: 0;
    overflow-wrap: anywhere;
}
.investigation-body .rx-body > .row {
    flex: 1 1 160px;
    min-width: 0;
}
.investigation-body .rx-body .row > * {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100% !important;
}
.investigation-body .rx-body .row button {
    width: 100%;
    max-width: 100%;
    text-align: left;
    white-space: normal;
    overflow-wrap: anywhere;
}
.dose button {
    padding: 2px 8px;
    font-size: 12px;
    color: #6e6b7b;
    background: #f1f0f5;
    border-radius: 5px;
    margin-top: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    transition: background 0.15s ease, color 0.15s ease;
}
.dose button:hover {
    background: #e9e7f8;
}
.dose button:hover {
    color: #7367f0;
}
.remarks button:hover {
    color: #7367f0;
}
.duration button:hover {
    color: #7367f0;
}
.rx-body {
    padding: 6px 10px;
    border-radius: 6px;
    transition: background 0.15s ease;
}
.rx-body:hover {
    background: #f8f8fa;
}
.note-input{
    position: absolute;
    width: 78%;
    padding: 0.271rem 1rem;
    display: inherit;
    top: -5px;
    left: 3%;
}
.note-body .card-body{
    padding: 4px 4px 8px;
}
.note-head {
    display: flex;
    margin-top: 0;
    justify-content: space-between;
    margin-bottom: 0;
    padding: 10px 14px;
    align-items: center;
    gap: 10px;
}
.action .close {
    color: #ea5455;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease;
}
.rx-body:hover .action .close {
    opacity: 1;
}
.note-body .card {
    background: transparent;
    border: none;
    box-shadow: none;
    margin-bottom: 0;
}
.note-body .card-body:empty {
    padding: 0;
}
.medicine {
    display: flex;
    justify-content: space-between;
    margin: 0;
    align-items: center;
    position: relative;
    font-size: 13px;
    color: #5e5873;
}
.medicine .sr-no {
    color: #b9b9c3;
    font-weight: 500;
}
.note-title .title-main {
    margin-bottom: 0;
    white-space: nowrap;
}
.note-head.rx-head {
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 14px;
    margin-top: 0;
    margin-bottom: 0;
}
.note-head.rx-head .title-main {
    font-size: 14px;
    font-weight: 600;
    color: #5e5873;
    letter-spacing: 0.2px;
    flex: 0 0 130px;
}
.rx-search-wrapper {
    position: relative;
    width: 45%;
    margin: 0 12px;
}
.rx-head .note-title {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    flex-wrap: wrap;
    gap: 10px;
}
.rx-head.investigation-head .note-title {
    gap: 8px;
}
.rx-head .rx-search-wrapper {
    flex: 1 1 220px;
    width: auto;
    min-width: 0;
    margin: 0;
}
.rx-head .select-template {
    flex: 1 1 180px;
    width: auto;
    min-width: 0;
}
.rx-head.investigation-head .title-main {
    font-size: 14px;
    white-space: normal;
    flex: 0 0 130px;
}
.rx-head.investigation-head .rx-search-wrapper,
.rx-head.investigation-head .select-template {
    flex: 1 1 130px;
}
.rx-head .input-group {
    border-radius: 8px;
    background: #f8f8fa;
    border: 1px solid #e4e2ec;
    min-height: 38px;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.rx-head .input-group:hover {
    border-color: #d8d6de;
}
.rx-head .input-group:focus-within {
    background: #ffffff;
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}
.rx-head .input-group .input-group-text {
    background: transparent;
    border: none;
    padding-right: 4px;
    opacity: 0.55;
}
.rx-head .input-group .form-control {
    background: transparent;
    border: none;
    padding-left: 4px;
    font-size: 13px;
    box-shadow: none;
}
.rx-head .input-group .form-control::placeholder {
    color: #b9b9c3;
}
.select-template {
    width: 38%;
}
.rx-head .select-template :deep(.vs__dropdown-toggle) {
    border-radius: 8px;
    background: #f8f8fa;
    border: 1px solid #e4e2ec !important;
    min-height: 38px;
    padding: 3px 4px;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.rx-head .select-template :deep(.vs__dropdown-toggle:hover) {
    border-color: #d8d6de !important;
}
.rx-head .select-template :deep(.vs__selected-options) {
    padding-left: 6px;
}
.rx-head .select-template :deep(.vs__actions) {
    padding-right: 8px;
}
.rx-head .select-template :deep(.vs__open-indicator) {
    fill: #6e6b7b;
    transform: scale(0.9);
}
.rx-head .note-btn {
    width: auto;
    margin-left: 0;
    flex: 0 0 auto;
}
.rx-head .select-template :deep(.vs--open .vs__dropdown-toggle) {
    background: #ffffff;
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}
.rx-head .select-template :deep(.vs__search::placeholder) {
    color: #b9b9c3;
    font-size: 13px;
}
.rx-add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: 8px;
    box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.35);
    transition: box-shadow 0.2s ease, transform 0.1s ease;
}
.rx-add-btn:hover {
    box-shadow: 0 4px 10px 0 rgba(115, 103, 240, 0.45);
}
.rx-add-btn:active {
    transform: translateY(1px);
}
hr {
    display: none;
}
.inline-product-list {
    position: absolute;
    width: 100%;
    max-height: 220px;
    overflow-y: auto;
    padding: 4px;
    margin: 6px 0 0;
    background: #ffffff;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    box-shadow: 0 5px 20px 0 rgba(34, 41, 47, 0.12);
    list-style: none;
    z-index: 99999;
}
.product-item {
    padding: 7px 8px;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.15s ease;
    word-break: break-word;
}
.product-item:hover {
    background: #f8f8fa;
}
.product-item .form-check-label {
    cursor: pointer;
}
.empty-text {
    padding: 12px 8px;
    text-align: center;
    color: #b9b9c3;
    font-size: 13px;
}
@media (max-width: 1199px) {
    .note-head.rx-head .title-main {
        flex: 1 1 100%;
    }
    .rx-head .rx-search-wrapper,
    .rx-head .select-template {
        flex: 1 1 100%;
    }
}
@media (max-width: 767px) {
    .note-head.rx-head {
        padding: 10px 12px;
    }
    .rx-head .note-title {
        gap: 8px;
    }
}
</style>